import _ from "lodash";
import { ISearchHandelssortimentModel } from "~app/features/forsaljning/handelssortiment/handelssortiment.models";
import { PristabellRad } from "../../models/pristabell/pristabell-models";
import { SortimentSnittnettoRad, Vardeberakning } from "../../models/vardeberakning/vardeberakning-models";

export type PristabellNode = {
    element: PristabellRad;
    children: PristabellRad[];
};

export const initPristabell = (
    pristabell: PristabellRad[],
    handelssortiment: ISearchHandelssortimentModel[],
    vardeberakning: Vardeberakning,
    recalculateValues = true,
    useApris = false) => {
    pristabell.forEach(rad => {
        if(!_.isNil(vardeberakning) && (recalculateValues || !rad.id)) {
            const snittnettoPredicate: (rad: SortimentSnittnettoRad) => boolean = rad.handelssortiment_Id
                ? x => rad.handelssortiment_Id === x.handelssortimentId
                : x => rad.handelssortimentsgrupp_Id === x.handelssortimentsgruppId;

            const snittnettoRad = vardeberakning.sortimentSnittnetto.rader.find(snittnettoPredicate);
            rad.initSortimentsnetto(snittnettoRad, useApris);
        }

        const hs = handelssortiment.find(x => x.id === rad.handelssortiment_Id);
        rad.initHandelssortiment(hs);
    });

    return sortPristabell(pristabell);
};

// sorterings funktion som används inom en grupp;
// 1. handelssortimentsgrupp hamnar först
// 2. autokompletterade rader
// 3. alfabetisk ordning
// 4. extra måttslag rader
// 5. kopierade rader

const sortFn = (a: PristabellRad, b: PristabellRad) => {
    if(a.isHsGrupp !== b.isHsGrupp) {
        return a.isHsGrupp ? -1 : 1;
    }

    if(a.autokompletterad !== b.autokompletterad) {
        return a.autokompletterad ? -1 : 1;
    }

    if(a.handelssortimentNamn !== b.handelssortimentNamn) {
        return (a.handelssortimentNamn ?? "").localeCompare(
            b.handelssortimentNamn ?? "",
            "sv-SE",
            { sensitivity: "base" }
        );
    }

    if(a.extraMattslagsrad !== b.extraMattslagsrad) {
        return a.extraMattslagsrad ? 1 : -1;
    }

    if(a.kopieradRad && !b.kopieradRad) {
        return 1;
    } else {
        return -1;
    }
};

export const sortPristabell = (pristabell: PristabellRad[]) => {
    // konstruerar träd, rader inom en grupp ligger alltid ihop
    const nodes = _.chain(pristabell)
        .filter(x => !_.isNil(x.handelssortimentsgrupp_Id))
        .groupBy(x => x.handelssortimentsgrupp_Id)
        .forEach((elements) => {
            elements.sort(sortFn);
        })
        .map((elements) => {
            const [
                element,
                ...children
            ] = elements;
            const node: PristabellNode = { element,
                children };
            return node;
        })
        .value();

    // sorterar grupperna efter deras sekvensnummer, plattar ut trädet
    const sortedTable = _.chain(nodes)
        .sortBy(node => node.element.handelssortimentsgruppSekvensnummer)
        .flatMap(node => [
            node.element,
            ...node.children
        ])
        .value();

    // rader som undantas från sorteringen, t.ex. snittnetto
    const rest = pristabell.filter(x =>
        !_.includes(sortedTable, x));

    return [
        ...rest,
        ...sortedTable
    ];
};
